import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  Alert
} from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';

interface PartialPaymentInputProps {
  totalAmount: number;
  paidAmount: number;
  onPaidAmountChange: (amount: number) => void;
  disabled?: boolean;
}

const PartialPaymentInput: React.FC<PartialPaymentInputProps> = ({
  totalAmount,
  paidAmount,
  onPaidAmountChange,
  disabled = false
}) => {
  const remainingAmount = totalAmount - paidAmount;
  const isFullyPaid = paidAmount >= totalAmount;
  const isPartialPayment = paidAmount > 0 && paidAmount < totalAmount;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    // التأكد من أن المبلغ لا يتجاوز الإجمالي
    const validAmount = Math.min(Math.max(0, value), totalAmount);
    onPaidAmountChange(validAmount);
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
      <Box display="flex" alignItems="center" mb={2}>
        <PaymentIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">الدفع الجزئي</Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={2}>
        {/* حقل إدخال المبلغ المدفوع */}
        <TextField
          fullWidth
          label="المبلغ المدفوع"
          type="number"
          value={paidAmount || ''}
          onChange={handleAmountChange}
          disabled={disabled}
          InputProps={{
            startAdornment: <InputAdornment position="start">ر.س</InputAdornment>,
            inputProps: { 
              min: 0, 
              max: totalAmount,
              step: 0.01
            }
          }}
          helperText={`الحد الأقصى: ${totalAmount.toFixed(2)} ر.س`}
        />

        {/* عرض ملخص الدفع */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1,
          p: 2,
          bgcolor: 'white',
          borderRadius: 1,
          border: '1px solid #e0e0e0'
        }}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              إجمالي الفاتورة:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {totalAmount.toFixed(2)} ر.س
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              المبلغ المدفوع:
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {paidAmount.toFixed(2)} ر.س
            </Typography>
          </Box>

          <Box 
            display="flex" 
            justifyContent="space-between"
            sx={{ 
              pt: 1, 
              borderTop: '1px solid #e0e0e0' 
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              المبلغ المتبقي:
            </Typography>
            <Typography 
              variant="body1" 
              fontWeight="bold"
              color={remainingAmount > 0 ? 'error.main' : 'success.main'}
            >
              {remainingAmount.toFixed(2)} ر.س
            </Typography>
          </Box>
        </Box>

        {/* رسائل التنبيه */}
        {isFullyPaid && (
          <Alert severity="success" icon={false}>
            تم دفع المبلغ بالكامل
          </Alert>
        )}

        {isPartialPayment && (
          <Alert severity="warning" icon={false}>
            دفع جزئي - المبلغ المتبقي: {remainingAmount.toFixed(2)} ر.س
          </Alert>
        )}

        {paidAmount === 0 && (
          <Alert severity="info" icon={false}>
            فاتورة آجلة - لم يتم الدفع
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default PartialPaymentInput;
